import React from "react";
import Grid from "@mui/material/Grid2";
import Switch from "@mui/material/Switch";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';

const App = (props) => {
    const theme = createTheme({
        palette: {
            primary: {
                main: props.primarycolor || "#1976d2",
            },
            secondary: {
                main: props.secondarycolor || "#dc004e",
            },
            tertiary: {
                main: props.tertiarycolor || "#ff0000",
            },
        },
        typography: {
            fontFamily: props.font || "Roboto, sans-serif",
        },
    });
    
    const params = new URLSearchParams(window.location.search);
    const initialPanel = parseInt(params.get("panelSize")) || 12;
    const [panelCount, setPanelCount] = React.useState(initialPanel);
    
    const panelData = {
        4:  { assumedCost: 5750, finalCost: 4518, netLifetimeSavings: 7057,  financeRepayments: 50.41 },
        8:  { assumedCost: 7450, finalCost: 5750, netLifetimeSavings: 17375, financeRepayments: 65.88 },
        10: { assumedCost: 8100, finalCost: 6300, netLifetimeSavings: 22228, financeRepayments: 73.62 },
        12: { assumedCost: 8900, finalCost: 7100, netLifetimeSavings: 27600, financeRepayments: 81.35 },
        14: { assumedCost: 9250, finalCost: 7450, netLifetimeSavings: 33425, financeRepayments: 87.35 },
        16: { assumedCost: 10000, finalCost: 8200, netLifetimeSavings: 38850, financeRepayments: 93.35 },
    };

    function calculateSolarData(panels) {
        if (panelData[panels]) {
            const data = panelData[panels];
            return {
                totalLifetimeSavings: data.netLifetimeSavings,
                initialOutlay: -data.finalCost,
                financeRepayments: data.financeRepayments,
                netSavings: data.netLifetimeSavings,
            };
        } else {
            // Interpolate for non-listed panel counts
            const keys = Object.keys(panelData).map(Number).sort((a,b) => a-b);
            let lower = keys[0], upper = keys[keys.length-1];
            for (let i = 0; i < keys.length; i++) {
                if (keys[i] < panels) lower = keys[i];
                if (keys[i] > panels) { upper = keys[i]; break; }
            }
            const ratio = (panels - lower) / (upper - lower);
            function lerp(a, b) { return a + ratio * (b - a); }
            const lowerData = panelData[lower];
            const upperData = panelData[upper];
            return {
                totalLifetimeSavings: lerp(lowerData.netLifetimeSavings, upperData.netLifetimeSavings),
                initialOutlay: -lerp(lowerData.finalCost, upperData.finalCost),
                financeRepayments: lerp(lowerData.financeRepayments, upperData.financeRepayments),
                netSavings: lerp(lowerData.netLifetimeSavings, upperData.netLifetimeSavings),
            };
        }
    }
    
    const selectedData = calculateSolarData(panelCount);
    const [withFinance, setWithFinance] = React.useState(true);
    const handleSwitchChange = (e) => setWithFinance(e.target.checked);
    
    const formatEuro = (value) => {
        const absValue = Math.abs(value);
        let formatted;
        if (Number.isInteger(absValue)) {
            formatted = absValue.toLocaleString();
        } else {
            formatted = absValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
        return value < 0 ? `- €${formatted}` : `€${formatted}`;
    };
    
    const monthlyElectricityBillSavings = selectedData.totalLifetimeSavings / (25 * 12);
    const displayedNetSavings = withFinance 
        ? selectedData.totalLifetimeSavings - (selectedData.financeRepayments * 12 * 10)
        : selectedData.totalLifetimeSavings + selectedData.initialOutlay;
    
    const localInitialOutlay = withFinance ? 0 : selectedData.initialOutlay;
    const localFinanceRepayments = withFinance ? selectedData.financeRepayments : 0;
    
    const graphFinalCost = withFinance ? 0 : selectedData.initialOutlay;
    const graphNetSavings = withFinance ? selectedData.netSavings - (selectedData.financeRepayments * 12 * 10) : selectedData.netSavings;
    
    const xData = [];
    for (let t = 0; t <= 25; t++) {
      xData.push(t);
    }
    
    const chartData = xData.map(t => {
      if (!withFinance) {
        return selectedData.initialOutlay + (selectedData.netSavings - selectedData.initialOutlay) * (t / 25);
      } else {
        const v0 = 0;
        const v10 = (selectedData.netSavings / 25) * 10 - (selectedData.financeRepayments * 12 * 10);
        const v25 = selectedData.netSavings - (selectedData.financeRepayments * 12 * 10);
        if (t <= 10) {
          return v0 + (v10 - v0) * (t / 10);
        } else {
          return v10 + (v25 - v10) * ((t - 10) / 15);
        }
      }
    });
    
    const finalCost = localInitialOutlay;
    const netLifetimeSavings = selectedData.netSavings;
    
    const [openModal, setOpenModal] = React.useState(false);
    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);
    
  	return (
        <ThemeProvider theme={theme}>
      		<div id="react-wp-app">
                <Grid container spacing={2} alignItems="center">
                    <Grid item size={{md: 6, xs: 12}}>
                        <h3>Estimated Return on Investment</h3>
						<p>This is an estimated graph showing the cost of your system and your savings over the lifetime of your system.</p>
                        <Switch color="primary" checked={withFinance} onChange={handleSwitchChange} /> <span>with Finance</span>
                        <TableContainer style={{ marginTop: "20px", marginBottom: "20px" }}>
                            <Table>
                                <TableBody>
									<TableRow>
                                        <TableCell>No. of panels</TableCell>
                                        <TableCell align="right">
											 <select 
												value={panelCount} 
												onChange={(e)=> setPanelCount(parseInt(e.target.value))}
												style={{ height: "30px" }} 
											>
												{[4,6,8,10,12,14,16].map(num => (
													<option key={num} value={num}>{num}</option>
												))}
											</select>
										</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Initial outlay</TableCell>
                                        <TableCell 
                                            align="right" 
                                            style={{ color: localInitialOutlay < 0 ? 'red' : 'black' }}
                                        >
                                            {formatEuro(localInitialOutlay)}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Monthly finance repayments</TableCell>
                                        <TableCell align="right">{formatEuro(localFinanceRepayments)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Monthly electricity bill savings</TableCell>
                                        <TableCell align="right">{formatEuro(monthlyElectricityBillSavings)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Total lifetime savings</TableCell>
                                        <TableCell align="right">{formatEuro(selectedData.totalLifetimeSavings)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ fontSize: '18px', fontWeight: 'bold' }}>Total Return on Investment</TableCell>
                                        <TableCell align="right" style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                            {formatEuro(displayedNetSavings)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
						<a style={{ color: theme.palette.primary.main, textDecoration: 'underline', cursor: 'pointer' }} onClick={handleOpenModal}>
							View assumptions
						</a>
					</Grid>
                    <Grid item size={{md: 6, xs: 12}}>
                        <LineChart
                            slotProps={{ legend: { hidden: true } }}
							xAxis={[{ 
                                data: xData,
                                valueFormatter: (value) => value === 0 ? '' : `${value} years`,
                                max: 25
                             }]}
							series={[{
								data: chartData,
								area: true,
								areaOpacity: 0.3,
								curve: 'linear',
                                label: 'ROI',
                                valueFormatter: (value) => `${value < 0 ? '- €' : '€'}${Math.abs(value).toLocaleString()}`,
                                showMark: false
							}]}
							yAxis={[{
                                valueFormatter: (value) => `${value < 0 ? '- €' : '€'}${Math.abs(value).toLocaleString()}`,
								colorMap: {
								  type: 'piecewise',
								  thresholds: [0],
								  colors: ['#d6a5a5', '#A5D6A7'],
								  areaOpacity: 0.3,
								}
							  }]}
							height={450}
                            margin={{ left: 70  }}
                        >
                            <ChartsReferenceLine 
                                y={7000} 
                                lineStyle={{ strokeDasharray: '10 5', stroke: '#aaa' }} 
                                label="7-Day Family Holiday to Lanzarote"
                                labelStyle={{ fill: '#555', fontSize: 12 }}
                                labelAlign="start"
                            />
                            <ChartsReferenceLine 
                                y={14900} 
                                lineStyle={{ strokeDasharray: '10 5', stroke: '#aaa' }}
                                label="10-Year Premium Ticket To Croke Park" 
                                labelStyle={{ fill: '#555', fontSize: 12 }}
                                labelAlign="start"
                            />
                        </LineChart>
                    </Grid>
                </Grid>
                <Modal open={openModal} onClose={handleCloseModal}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '80%', sm: 400 },
                        bgcolor: 'background.paper',
                        border: 'none',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        outline: 'none'
                    }}>
                        <h4 style={{ marginTop: 0 }}>Assumptions</h4>
                        <p style={{ lineHeight: 1.5, marginBottom: 0 }}>Placeholder text for assumptions.</p>
                    </Box>
                </Modal>
      		</div>
        </ThemeProvider>
  	);
};

export default App;
